import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Dispatch } from '@reduxjs/toolkit';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { createRiskAccountAction, deleteRiskAccountAction } from 'redux/actions/riskActions';
import { AsyncAction } from 'redux/asyncAction';
import { riskSelector, setIsDeletingRiskAccount } from 'redux/reducers/riskReducer';
import { AllOCC, NewAccount, RiskAccount, RiskClearingEntry } from 'types/risk';

interface AccountFormState {
  headAccount: string;
  account: string;
  companyName: string;
  contacts: string;
  shortname: string;
  FDID: string;
  IMID: string;
  securityType: string;
  accountToDelete: string;
  allocationaccount: string;
  clearing: RiskClearingEntry[];
  /** @deprecated use clearing */
  clearingAccount?: string;
  /** @deprecated use clearing */
  occnum?: string;
}

const INITIAL_FORM_STATE: AccountFormState = {
  headAccount: '',
  account: '',
  companyName: '',
  contacts: '',
  shortname: '',
  FDID: '',
  IMID: '',
  securityType: '',
  accountToDelete: '',
  allocationaccount: '',
  clearing: [],
};

/** Matches compact account fields and Autocomplete list options */
const ACCOUNT_FORM_INPUT_FONT_SIZE = '0.8125rem';

/** Shared compact control height for account pickers and matching text fields */
const COMPACT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    minHeight: 26,
    height: 26,
    pl: 0,
    pr: '4px',
    alignItems: 'center',
    paddingTop: 0,
    paddingBottom: 0,
  },
  '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
    height: '100%',
    lineHeight: 1.2,
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingRight: 0,
    boxSizing: 'border-box' as const,
    fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE,
    paddingLeft: 0,
    marginLeft: 0,
  },
};

/** Applied to Autocomplete dropdown Paper (list + options) */
const AUTOCOMPLETE_DROPDOWN_SX = {
  '& .MuiAutocomplete-listbox': {
    fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE,
  },
  '& .MuiAutocomplete-option': {
    fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE,
    lineHeight: 1.25,
    minHeight: 'auto',
    py: '4px',
  },
};

/** Keeps popup trigger and input text vertically centered in compact fields */
const COMPACT_AUTOCOMPLETE_SX = {
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
    paddingTop: 0,
    paddingBottom: 0,
  },
  '& .MuiAutocomplete-popupIndicator': {
    p: 0,
    mr: 0,
  },
  '& .MuiAutocomplete-clearIndicator': {
    p: 0,
    mr: 0,
  },
};

const CLEARING_LIST_SX = {
  maxHeight: 250,
  overflowY: 'auto' as const,
  mt: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
};

const parseClearingAccountInput = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim().toUpperCase())
    .filter(Boolean);

const trimFormStateForSave = (state: AccountFormState): AccountFormState => ({
  ...state,
  headAccount: state.headAccount.trim(),
  account: state.account.trim(),
  companyName: state.companyName.trim(),
  contacts: state.contacts.trim(),
  shortname: state.shortname.trim(),
  FDID: state.FDID.trim(),
  IMID: state.IMID.trim(),
  accountToDelete: state.accountToDelete.trim(),
  allocationaccount: state.allocationaccount.trim(),
  clearingAccount: state.clearingAccount?.trim(),
  clearing: state.clearing.map((entry) => ({
    ...entry,
    occaccount: entry.occaccount.trim(),
  })),
});

const buildClearingEntriesFromAccount = (account: NewAccount): RiskClearingEntry[] => {
  if (account.clearing?.length) {
    return account.clearing.map((entry) => ({
      occ: Number(entry.occ),
      occaccount: entry.occaccount,
    }));
  }

  if (account.clearingaccount && account.occnum !== undefined) {
    return [{ occ: Number(account.occnum), occaccount: account.clearingaccount }];
  }

  return [];
};

const groupClearingEntriesByOcc = (
  clearing: RiskClearingEntry[]
): Array<[number, RiskClearingEntry[]]> => {
  const groups = new Map<number, RiskClearingEntry[]>();

  clearing.forEach((entry) => {
    const group = groups.get(entry.occ) ?? [];
    group.push(entry);
    groups.set(entry.occ, group);
  });

  return Array.from(groups.entries()).sort(([occA], [occB]) => occA - occB);
};

const buildOCCMemberLookup = (members?: AllOCC[] | null): Map<number, AllOCC> => {
  const lookup = new Map<number, AllOCC>();

  members?.forEach((member) => {
    const occNumber = Number(member.occ);
    if (!Number.isNaN(occNumber)) {
      lookup.set(occNumber, member);
    }
  });

  return lookup;
};

const formatOCCMemberLabel = (occ: number, occMemberLookup: Map<number, AllOCC>): string => {
  const occMember = occMemberLookup.get(occ);
  if (occMember?.occ && occMember?.name) {
    return `${occMember.occ} - ${occMember.name}`;
  }
  return occ.toString();
};

const RequiredIndicator = ({ required }: { required: boolean }) => {
  if (!required) return null;
  return (
    <span style={{ color: 'red', marginLeft: 4 }} aria-hidden="true">
      *
    </span>
  );
};

interface Props {
  readonly readOnly: boolean;
}

export const AccountForm = ({ readOnly }: Props): JSX.Element => {
  const dispatch = useDispatch<Dispatch<AsyncAction | AnyAction>>();
  const risk = useSelector(riskSelector);
  const {
    clientID,
    savingRiskAccount,
    riskAccounts,
    deletingRiskAccount,
    isDeletingRiskAccount,
    allOCC,
    fetchingAllOCC,
    companiesWithMpids,
    fetchingCompaniesWithMpids,
  } = risk;

  const [formState, setFormState] = useState<AccountFormState>(INITIAL_FORM_STATE);
  const [includeOptions, setIncludeOptions] = useState(false);
  const [includeEquities, setIncludeEquities] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedOCC, setSelectedOCC] = useState<AllOCC>({ occ: '', name: '' });
  const sortedAccountIds = useMemo(
    () =>
      [...riskAccounts.accounts]
        .sort((a, b) =>
          a.account.localeCompare(b.account, undefined, { sensitivity: 'base', numeric: true })
        )
        .map((acc) => acc.account),
    [riskAccounts.accounts]
  );

  const companyNameOptions = useMemo(
    () => (companiesWithMpids ?? []).map((company) => company.name),
    [companiesWithMpids]
  );

  const shortnameOptions = useMemo(() => {
    const selectedCompany = companiesWithMpids?.find(
      (company) => company.name === formState.companyName
    );
    return selectedCompany?.mpids ? [...selectedCompany.mpids] : [];
  }, [companiesWithMpids, formState.companyName]);

  const transformAndValidateField = (
    name: string,
    value: string
  ): { transformedValue: string; error: string } => {
    let transformedValue = value;
    let error = '';

    switch (name) {
      case 'account':
        // Transform to uppercase
        transformedValue = value.toUpperCase();
        break;
      case 'clearingAccount':
        // Transform characters to uppercase
        transformedValue = value.toUpperCase();
        break;
      case 'occnum': {
        // Only allow numeric input and limit to 3 digits maximum
        const numericValue = value.replace(/\D/g, '');
        transformedValue = numericValue.slice(0, 3);
        break;
      }
      case 'allocationaccount': {
        // Transform characters to uppercase
        transformedValue = value.toUpperCase();
        break;
      }
      case 'shortname':
        // Transform to uppercase and limit to 4 characters
        transformedValue = value.toUpperCase().slice(0, 4);
        // Only show error if there's a value but it's not exactly 4 characters
        if (transformedValue.length > 0 && transformedValue.length !== 4) {
          error = 'Short name must be exactly 4 characters';
        }
        break;
      default:
        transformedValue = value;
    }

    return { transformedValue, error };
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const { transformedValue, error } = transformAndValidateField(name, value);

    setFormState((prev) => ({ ...prev, [name]: transformedValue }));
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const applyAccountSelectionFromId = (accountId: string) => {
    const accountToEdit = riskAccounts.accounts.find((a) => a.account === accountId);
    if (!accountToEdit) return;

    const clearing = buildClearingEntriesFromAccount(accountToEdit);
    const occValue = accountToEdit.occnum?.toString() ?? clearing[0]?.occ?.toString() ?? '';
    const matchedOCC = allOCC?.find(
      (member) => member.occ === occValue || Number(member.occ) === Number(occValue)
    );

    setSelectedOCC(matchedOCC ?? (occValue ? { occ: occValue, name: '' } : { occ: '', name: '' }));
    setFormState({
      account: accountToEdit.account,
      headAccount: accountToEdit.parent_account,
      companyName: accountToEdit.company,
      contacts: accountToEdit.contacts,
      shortname: accountToEdit.shortname,
      FDID: accountToEdit.fdid,
      IMID: accountToEdit.imid,
      securityType: accountToEdit.security_type,
      allocationaccount: accountToEdit.allocationaccount?.toString() ?? '',
      accountToDelete: '',
      clearing,
      clearingAccount: '',
      occnum: occValue,
    });
    if (accountToEdit.security_type === 'OPT') {
      setIncludeOptions(true);
      setIncludeEquities(false);
    }
    if (accountToEdit.security_type === 'EQ') {
      setIncludeOptions(false);
      setIncludeEquities(true);
    }
    if (accountToEdit.security_type === 'BOTH') {
      setIncludeOptions(true);
      setIncludeEquities(true);
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setIsEditing(e.target.checked);
    setFormState(INITIAL_FORM_STATE);
    setIncludeOptions(false);
    setIncludeEquities(false);
    setSelectedOCC({ occ: '', name: '' });
    dispatch(setIsDeletingRiskAccount(false));
    setErrors({});
  };

  const handleAddClearingAccounts = (): void => {
    const occ = Number(selectedOCC.occ);
    if (!selectedOCC.occ || Number.isNaN(occ)) return;

    const accounts = parseClearingAccountInput(formState.clearingAccount ?? '');
    if (accounts.length === 0) return;

    setFormState((prev) => {
      const existing = new Set(prev.clearing.map((entry) => `${entry.occ}:${entry.occaccount}`));
      const newEntries = accounts
        .filter((occaccount) => !existing.has(`${occ}:${occaccount}`))
        .map((occaccount) => ({ occ, occaccount }));

      return {
        ...prev,
        clearing: [...prev.clearing, ...newEntries],
        clearingAccount: '',
      };
    });
  };

  const handleRemoveClearingEntry = (entryToRemove: RiskClearingEntry): void => {
    setFormState((prev) => ({
      ...prev,
      clearing: prev.clearing.filter(
        (entry) =>
          !(entry.occ === entryToRemove.occ && entry.occaccount === entryToRemove.occaccount)
      ),
    }));
  };

  const handleCheckboxChange = (name: string, value: boolean) => {
    const newIncludeOptions = name === 'includeOptions' ? value : includeOptions;
    const newIncludeEquities = name === 'includeEquities' ? value : includeEquities;

    setIncludeOptions(newIncludeOptions);
    setIncludeEquities(newIncludeEquities);

    const securityType =
      newIncludeOptions && newIncludeEquities
        ? 'BOTH'
        : newIncludeOptions
        ? 'OPT'
        : newIncludeEquities
        ? 'EQ'
        : '';

    setFormState((prev) => ({ ...prev, securityType }));
  };

  const onSubmit = () => {
    const trimmedFormState = trimFormStateForSave(formState);

    if (isDeletingRiskAccount) {
      dispatch(deleteRiskAccountAction(trimmedFormState.accountToDelete as string, clientID));
    } else {
      const payload = {
        riskengineid: clientID,
        accounts: [
          {
            account: trimmedFormState.account,
            parent_account: trimmedFormState.headAccount,
            company: trimmedFormState.companyName,
            shortname: trimmedFormState.shortname,
            imid: trimmedFormState.IMID,
            clearingaccount:
              trimmedFormState.clearing[0]?.occaccount ?? trimmedFormState.clearingAccount ?? '',
            occnum: trimmedFormState.clearing[0]?.occ ?? Number(trimmedFormState.occnum ?? 0),
            contacts: trimmedFormState.contacts,
            security_type: trimmedFormState.securityType,
            fdid: trimmedFormState.FDID,
            allocationaccount: trimmedFormState.allocationaccount,
            clearing: trimmedFormState.clearing,
          },
        ],
      } as RiskAccount;

      dispatch(createRiskAccountAction(payload));

      setFormState(INITIAL_FORM_STATE);
      setIncludeOptions(false);
      setIncludeEquities(false);
      setSelectedOCC({ occ: '', name: '' });
      setErrors({});
    }
  };

  const handleCancel = () => {
    setFormState(INITIAL_FORM_STATE);
    setIncludeOptions(false);
    setIncludeEquities(false);
    setSelectedOCC({ occ: '', name: '' });
    dispatch(setIsDeletingRiskAccount(false));
    setIsEditing(false);
    setErrors({});
  };

  const groupedClearingEntries = useMemo(
    () => groupClearingEntriesByOcc(formState.clearing),
    [formState.clearing]
  );

  const occMemberLookup = useMemo(() => {
    const lookup = buildOCCMemberLookup(allOCC);

    if (selectedOCC.occ) {
      const occNumber = Number(selectedOCC.occ);
      if (!Number.isNaN(occNumber) && selectedOCC.name) {
        lookup.set(occNumber, selectedOCC);
      }
    }

    return lookup;
  }, [allOCC, selectedOCC]);

  const hasErrors = Object.keys(errors).some((key) => errors[key] !== '');
  const disableOnCreate =
    !isDeletingRiskAccount &&
    !isEditing &&
    (savingRiskAccount || !formState.account || !formState.securityType || hasErrors);
  const disableOnDelete = deletingRiskAccount || !formState.accountToDelete;
  const disableOnEdit = !formState.account || hasErrors;
  const hasSelectedOCC = Boolean(selectedOCC.occ);
  const hasPendingClearingAccounts = Boolean(formState.clearingAccount?.trim());

  if (readOnly) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>You do not have permission to manage risk accounts.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Account Form
        </Typography>

        <FormControlLabel
          control={<Switch checked={isEditing} onChange={handleSwitchChange} />}
          label="Edit account"
        />
        <FormControlLabel
          control={
            <Switch
              checked={isDeletingRiskAccount}
              onChange={() => {
                dispatch(setIsDeletingRiskAccount(!isDeletingRiskAccount));
                setFormState(INITIAL_FORM_STATE);
                setIsEditing(false);
                setErrors({});
              }}
            />
          }
          label="Delete account"
        />
      </Stack>
      <form onSubmit={(e) => e.preventDefault()}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {!isDeletingRiskAccount ? (
              <>
                <Typography variant="caption" gutterBottom>
                  Account <RequiredIndicator required={true} />
                </Typography>
                {isEditing ? (
                  <Autocomplete
                    fullWidth
                    disableClearable
                    options={sortedAccountIds}
                    value={formState.account ? formState.account : null}
                    onChange={(_, newValue) => {
                      if (newValue) applyAccountSelectionFromId(newValue);
                    }}
                    componentsProps={{
                      paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                    }}
                    sx={COMPACT_AUTOCOMPLETE_SX}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        name="account"
                        id="account"
                        placeholder="SELECT ACCOUNT"
                        sx={COMPACT_FIELD_SX}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    name="account"
                    id="account"
                    value={formState.account || ''}
                    onChange={onChange}
                    placeholder="ENTER ACCOUNT"
                    required
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                )}

                <Typography variant="caption" gutterBottom>
                  Head Account
                </Typography>
                <Autocomplete
                  fullWidth
                  disableClearable
                  options={sortedAccountIds}
                  value={formState.headAccount ? formState.headAccount : null}
                  onChange={(_, newValue) => {
                    setFormState((prev) => ({ ...prev, headAccount: newValue ?? '' }));
                  }}
                  componentsProps={{
                    paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                  }}
                  sx={COMPACT_AUTOCOMPLETE_SX}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      name="headAccount"
                      id="headAccount"
                      placeholder="SELECT HEAD ACCOUNT"
                      sx={COMPACT_FIELD_SX}
                    />
                  )}
                />

                <Typography variant="caption" gutterBottom>
                  Company Name <RequiredIndicator required={false} />
                </Typography>
                <Autocomplete
                  fullWidth
                  disableClearable
                  options={companyNameOptions}
                  value={formState.companyName ? formState.companyName : null}
                  onChange={(_, newValue) => {
                    setFormState((prev) => ({
                      ...prev,
                      companyName: newValue ?? '',
                      shortname: '',
                    }));
                    setErrors((prev) => ({ ...prev, shortname: '' }));
                  }}
                  loading={fetchingCompaniesWithMpids}
                  componentsProps={{
                    paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                  }}
                  sx={COMPACT_AUTOCOMPLETE_SX}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="companyName"
                      id="companyName"
                      placeholder="SELECT COMPANY NAME"
                      sx={COMPACT_FIELD_SX}
                    />
                  )}
                />

                <Typography variant="caption" gutterBottom>
                  Contacts <RequiredIndicator required={false} />
                </Typography>
                <TextField
                  fullWidth
                  name="contacts"
                  id="contacts"
                  value={formState.contacts || ''}
                  onChange={onChange}
                  placeholder="ENTER CONTACTS"
                />

                <Typography variant="caption" gutterBottom>
                  Allocation Account <RequiredIndicator required={false} />
                </Typography>
                <TextField
                  fullWidth
                  name="allocationaccount"
                  id="allocationaccount"
                  value={formState.allocationaccount || ''}
                  onChange={onChange}
                  placeholder="ENTER ALLOCATION ACCOUNT"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
                <Typography variant="caption" gutterBottom>
                  OCC Members <RequiredIndicator required={false} />
                </Typography>
                <Autocomplete<AllOCC, false, true, false>
                  fullWidth
                  options={allOCC ?? []}
                  getOptionLabel={(option) =>
                    option.occ && option.name ? `${option.occ} - ${option.name}` : ''
                  }
                  value={selectedOCC}
                  onChange={(_, newValue) => {
                    setSelectedOCC(newValue);
                    setFormState((prev) => ({
                      ...prev,
                      occnum: newValue.occ,
                      clearingAccount: '',
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option.occ === value.occ}
                  loading={fetchingAllOCC}
                  componentsProps={{
                    paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                  }}
                  disableClearable
                  sx={COMPACT_AUTOCOMPLETE_SX}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="occMembers"
                      id="occMembers"
                      placeholder="SEARCH OCC MEMBERS"
                      sx={COMPACT_FIELD_SX}
                    />
                  )}
                />

                <Typography variant="caption" gutterBottom>
                  OCC Clearing Account <RequiredIndicator required={false} />
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TextField
                    fullWidth
                    name="clearingAccount"
                    id="clearingAccount"
                    value={formState.clearingAccount ?? ''}
                    onChange={onChange}
                    placeholder="ENTER CLEARING ACCOUNTS (COMMA SEPARATED)"
                    disabled={!hasSelectedOCC}
                    margin="none"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddClearingAccounts}
                    disabled={!hasSelectedOCC || !hasPendingClearingAccounts}
                    sx={{ flexShrink: 0 }}
                  >
                    Add
                  </Button>
                </Stack>
                {formState.clearing.length > 0 ? (
                  <Box sx={CLEARING_LIST_SX}>
                    {groupedClearingEntries.map(([occ, entries]) => (
                      <Box key={occ}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            px: 1,
                            py: 0.5,
                            fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE,
                            fontWeight: 600,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'action.hover',
                          }}
                        >
                          {formatOCCMemberLabel(occ, occMemberLookup)}
                        </Typography>
                        {entries.map((entry) => (
                          <Box
                            key={`${entry.occ}-${entry.occaccount}`}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                            px={1}
                            py={0.5}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE }}
                            >
                              {entry.occaccount}
                            </Typography>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleRemoveClearingEntry(entry)}
                              sx={{
                                minWidth: 'auto',
                                px: 1,
                                fontSize: ACCOUNT_FORM_INPUT_FONT_SIZE,
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </>
            ) : (
              <>
                <Typography variant="caption" gutterBottom>
                  Account to delete
                </Typography>
                <Autocomplete
                  fullWidth
                  disableClearable
                  options={sortedAccountIds}
                  value={formState.accountToDelete ? formState.accountToDelete : null}
                  onChange={(_, newValue) => {
                    setFormState((prev) => ({ ...prev, accountToDelete: newValue ?? '' }));
                  }}
                  componentsProps={{
                    paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                  }}
                  sx={COMPACT_AUTOCOMPLETE_SX}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      name="accountToDelete"
                      id="accountToDelete"
                      placeholder="SELECT ACCOUNT TO DELETE"
                      sx={COMPACT_FIELD_SX}
                    />
                  )}
                />
              </>
            )}
          </Grid>

          {!isDeletingRiskAccount ? (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" gutterBottom>
                Short Name <RequiredIndicator required={false} />
              </Typography>
              <Autocomplete
                fullWidth
                disableClearable
                options={shortnameOptions}
                value={formState.shortname ? formState.shortname : null}
                onChange={(_, newValue) => {
                  setFormState((prev) => ({ ...prev, shortname: newValue ?? '' }));
                  setErrors((prev) => ({ ...prev, shortname: '' }));
                }}
                disabled={!formState.companyName}
                loading={fetchingCompaniesWithMpids}
                componentsProps={{
                  paper: { sx: AUTOCOMPLETE_DROPDOWN_SX },
                }}
                sx={COMPACT_AUTOCOMPLETE_SX}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="shortname"
                    id="shortname"
                    placeholder="SELECT SHORT NAME"
                    error={!!errors.shortname}
                    helperText={errors.shortname}
                    sx={COMPACT_FIELD_SX}
                  />
                )}
              />
              <Typography variant="caption" gutterBottom>
                FDID <RequiredIndicator required={false} />
              </Typography>
              <TextField
                fullWidth
                name="FDID"
                id="FDID"
                value={formState.FDID || ''}
                onChange={onChange}
                placeholder="ENTER FDID"
              />

              <Typography variant="caption" gutterBottom>
                IMID <RequiredIndicator required={false} />
              </Typography>
              <TextField
                fullWidth
                name="IMID"
                id="IMID"
                value={formState.IMID || ''}
                onChange={onChange}
                placeholder="ENTER IMID"
              />

              <FormGroup sx={{ m: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="includeOptions"
                      checked={includeOptions}
                      onChange={(e) => handleCheckboxChange(e.target.name, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="subtitle1">
                      Include the account in options
                      <RequiredIndicator required={true} />
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="includeEquities"
                      checked={includeEquities}
                      onChange={(e) => handleCheckboxChange(e.target.name, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="subtitle1">
                      Include the account in equities
                      <RequiredIndicator required={true} />
                    </Typography>
                  }
                />
              </FormGroup>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2,
              }}
            >
              <Button variant="outlined" color="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={
                  disableOnCreate ||
                  (isDeletingRiskAccount && disableOnDelete) ||
                  (isEditing && disableOnEdit)
                }
                color="primary"
                onClick={onSubmit}
              >
                {savingRiskAccount || deletingRiskAccount ? (
                  <CircularProgress size={24} />
                ) : isDeletingRiskAccount ? (
                  'Delete'
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
